
import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  signal,
  viewChild,
} from '@angular/core';
import {
  BadgeComponent,
  ButtonComponent,
  DatepickerComponent,
  DialogService,
  EmptyComponent,
  InputComponent,
  SearchComponent,
  SelectComponent,
  TableComponent,
  type ActionEvent,
} from 'nebula-ui-kit';
import {
  VaultAppToastService,
  readVaultItemsPerPageFromPreferences,
  VAULT_TABLE_PAGE_SIZE_OPTIONS,
  VAULT_TABLE_PAGINATION_LABELS,
} from '@nebula/shared-vault/data-access';
import {
  bindVaultTablePageSizeToPreferences,
  createVaultTablePageSizeSignal,
} from '../vault-table-pagination-bind.util';
import {
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  PageHeaderComponent,
  openVaultConfirmDialog,
  scrollVaultEditPanelWhenReady,
  VaultFormValidationAlertComponent,
  VaultModuleShellComponent,
  VaultPageAlertComponent,
} from '@nebula-shared/ui';
import {
  LegalHoldDocument,
  type LegalHoldDocumentType,
} from '../../../../domain/src/lib/legal-hold/legal-hold.model';
import { type CaseForm, legalHoldStore, type LegalHoldRow } from '../../../../store/src/lib/legal-hold.store';
import {
  buildLegalHoldConfirmDialogLabel,
  buildLegalHoldConfirmDialogMessage,
  buildLegalHoldConfirmDialogTitle,
  canRequestLegalHoldClose,
  legalHoldCaseDisplayName,
  LEGAL_HOLD_DELETE_BLOCKED_TOAST,
  shouldBlockLegalHoldDelete,
  type LegalHoldPendingConfirm,
} from './legal-hold-action.utils';

@Component({
  selector: 'app-legal',
  templateUrl: './legal.component.html',
  styleUrls: ['./legal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    VaultModuleShellComponent,
    InputComponent,
    DatepickerComponent,
    SearchComponent,
    SelectComponent,
    ButtonComponent,
    BadgeComponent,
    TableComponent,
    EmptyComponent,
    GridFormComponent,
    GridFormItemComponent,
    FieldLabelComponent,
    VaultFormValidationAlertComponent,
    VaultPageAlertComponent,
  ],
})
export class LegalComponent {
  private readonly dialogService = inject(DialogService);
  private readonly vaultAppToast = inject(VaultAppToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly editPanel = viewChild<ElementRef<HTMLElement>>('editPanel');

  readonly store = inject(legalHoldStore);
  readonly loading = this.store.loading;
  readonly saving = this.store.saving;
  readonly error = this.store.error;
  readonly info = this.store.info;
  readonly rows = this.store.rows;
  readonly selected = this.store.selected;
  readonly statusFilter = this.store.statusFilter;
  readonly searchTerm = this.store.searchTerm;
  readonly assignmentDialogOpen = this.store.assignmentDialogOpen;
  readonly assignmentSearch = this.store.assignmentSearch;
  readonly assignmentType = this.store.assignmentType;
  readonly assignmentReason = this.store.assignmentReason;
  readonly selectedDocumentId = this.store.selectedDocumentId;
  readonly availableDocuments = this.store.availableDocuments;
  readonly statusOptions = this.store.statusOptions();
  readonly assignmentTypeOptions = this.store.assignmentTypeOptions();
  readonly creating = this.store.creating;
  readonly editingId = this.store.editingId;
  readonly form = this.store.form;
  readonly headerActions = this.store.headerActions;
  readonly tableConfig = this.store.tableConfig;
  readonly filteredRows = this.store.filteredRows;
  readonly selectedDocuments = this.store.selectedDocuments;
  readonly tablePageSize = createVaultTablePageSizeSignal();
  readonly tablePageSizeOptions = [...VAULT_TABLE_PAGE_SIZE_OPTIONS];

  constructor() {
    this.store.init();
    bindVaultTablePageSizeToPreferences(this.tablePageSize, this.destroyRef);
  }

  onHeaderAction(actionId: string): void {
    this.store.onHeaderAction(actionId);
  }

  onTableAction(ev: ActionEvent<LegalHoldRow>): void {
    const row = ev.data;
    if (!row) return;

    if (ev.action === 'view') {
      this.store.openDetail(row.id);
      return;
    }
    if (ev.action === 'edit') {
      this.store.startEdit(row);
      afterNextRender(
        () =>
          scrollVaultEditPanelWhenReady(() => this.editPanel()?.nativeElement ?? null),
        { injector: this.injector },
      );
      return;
    }
    if (ev.action === 'reopen') {
      this.store.changeCaseStatus(row.id, false);
      return;
    }
    if (ev.action === 'close') {
      this.confirmPendingAction(row, 'close');
      return;
    }
    if (ev.action === 'delete') {
      this.confirmPendingAction(row, 'delete');
    }
  }

  onRowAction(action: string, row: LegalHoldRow): void {
    this.onTableAction({ action, data: row });
  }

  onTablePageChange(ev: { pageIndex: number; pageSize: number }): void {
    if (ev.pageSize != null) this.tablePageSize.set(ev.pageSize);
  }

  onFormField<K extends keyof CaseForm>(key: K, value: CaseForm[K]): void {
    this.store.onFormField(key, value);
  }

  onSearchTermChange(value: string): void {
    this.store.onSearchTermChange(value);
  }

  onStatusFilterChange(value: string): void {
    this.store.onStatusFilterChange(value);
  }

  onAssignmentTypeChange(value: string): void {
    this.store.onAssignmentTypeChange(value);
  }

  onAssignmentSearchChange(value: string): void {
    this.store.onAssignmentSearchChange(value);
  }

  onAssignmentReasonChange(value: string): void {
    this.store.onAssignmentReasonChange(value);
  }

  selectDocumentOption(id: string): void {
    this.store.selectDocumentOption(id);
  }

  closeCaseForm(): void {
    this.store.closeCaseForm();
  }

  saveCase(): void {
    this.store.saveCase();
  }

  openAssignmentDialog(): void {
    this.store.openAssignmentDialog();
  }

  closeAssignmentDialog(): void {
    this.store.closeAssignmentDialog();
  }

  assignDocument(): void {
    this.store.assignDocument();
  }

  removeDocument(doc: LegalHoldDocument): void {
    this.store.removeDocument(doc);
  }

  private confirmPendingAction(row: LegalHoldRow, type: LegalHoldPendingConfirm['type']): void {
    if (type === 'close' && !canRequestLegalHoldClose(row.status)) {
      return;
    }
    if (type === 'delete' && shouldBlockLegalHoldDelete(row.status)) {
      this.vaultAppToast.show(LEGAL_HOLD_DELETE_BLOCKED_TOAST);
      return;
    }

    const pending: LegalHoldPendingConfirm = {
      type,
      id: row.id,
      case_name: legalHoldCaseDisplayName(row),
    };

    openVaultConfirmDialog(this.dialogService, {
      title: buildLegalHoldConfirmDialogTitle(type),
      message: buildLegalHoldConfirmDialogMessage(pending),
      confirmLabel: buildLegalHoldConfirmDialogLabel(type),
      variant: type === 'delete' ? 'error' : 'warning',
    }).subscribe((confirmed) => {
      if (confirmed) {
        this.store.executePendingAction(pending);
      }
    });
  }
}
